import { useSyncExternalStore } from "react";
import { MissionId, MISSIONS } from "./missions";

export interface SimState {
  completed: MissionId[];
  records: Record<string, string | number | boolean>;
}

const STORAGE_KEY = "ql.simulation.v1";

const VALID_MISSION_IDS = new Set<string>(MISSIONS.map((m) => m.id));

function getEmptyState(): SimState {
  return {
    completed: [],
    records: {},
  };
}

function parseState(raw: string | null): SimState {
  if (!raw) return getEmptyState();
  try {
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return getEmptyState();

    const rawCompleted = Array.isArray(data.completed) ? data.completed : [];
    const completed: MissionId[] = [];
    for (const item of rawCompleted) {
      if (typeof item === "string" && VALID_MISSION_IDS.has(item)) {
        if (!completed.includes(item as MissionId)) {
          completed.push(item as MissionId);
        }
      }
    }

    const records: Record<string, string | number | boolean> = {};
    if (data.records && typeof data.records === "object" && !Array.isArray(data.records)) {
      for (const [k, v] of Object.entries(data.records)) {
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          records[k] = v;
        }
      }
    }

    return { completed, records };
  } catch {
    return getEmptyState();
  }
}

// In-memory snapshot & subscriber set
let currentState: SimState = getEmptyState();
let initialized = false;
const listeners = new Set<() => void>();

function initStoreIfNeeded(): void {
  if (initialized) return;
  initialized = true;
  if (typeof window !== "undefined") {
    try {
      currentState = parseState(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      currentState = getEmptyState();
    }
  }
}

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function persist(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
  } catch {
    // Ignore storage quota errors
  }
}

export function completeMission(id: MissionId): void {
  initStoreIfNeeded();
  if (currentState.completed.includes(id)) {
    return;
  }
  currentState = {
    ...currentState,
    completed: [...currentState.completed, id],
  };
  persist();
  notify();
}

export function recordSimValue(key: string, value: string | number | boolean): void {
  initStoreIfNeeded();
  if (currentState.records[key] === value) {
    return;
  }
  currentState = {
    ...currentState,
    records: {
      ...currentState.records,
      [key]: value,
    },
  };
  persist();
  notify();
}

export function clearSimProgress(): void {
  initStoreIfNeeded();
  currentState = getEmptyState();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  }
  notify();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): SimState {
  initStoreIfNeeded();
  return currentState;
}

const SERVER_SNAPSHOT = getEmptyState();
function getServerSnapshot(): SimState {
  return SERVER_SNAPSHOT;
}

export interface SimProgressHook {
  completed: MissionId[];
  records: Record<string, string | number | boolean>;
  complete: (id: MissionId) => void;
  record: (key: string, value: string | number | boolean) => void;
  clear: () => void;
}

export function useSimProgress(): SimProgressHook {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    completed: state.completed,
    records: state.records,
    complete: completeMission,
    record: recordSimValue,
    clear: clearSimProgress,
  };
}
